import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,
  faTicketAlt,
  faInbox,
  faExclamationCircle,
  faTimes,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

/* ----------------------- tiny helpers ----------------------- */
const readUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return { SLIITID: "0", name: "Unknown User", role: "Member", image: "/user.png", ...u };
  } catch {
    return { SLIITID: "0", name: "Unknown User", role: "Member", image: "/user.png" };
  }
};

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;      // common wrapper
  if (Array.isArray(data?.items)) return data.items;    // common wrapper
  if (Array.isArray(data?.tickets)) return data.tickets;
  if (Array.isArray(data?.members)) return data.members;
  return [];
};

const safeDate = (v) => {
  const d = new Date(v);
  return Number.isFinite(d.valueOf()) ? d : null;
};

/* ----------------------- shared status pill ----------------------- */
const STATUS_PILL = {
  Open: "pill-warning",
  Pending: "pill-info",
  Review: "pill-info",
  Closed: "pill-success",
};

const StatusPill = ({ status }) => (
  <span className={`pill ${STATUS_PILL[status] || "pill-info"}`}>
    <span className="dot" />
    {status}
  </span>
);

/* ============================================================
   Tickets — Admin List
============================================================ */
function TicketListAdmin() {
  const user = readUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState({
    open: false,
    ticket: null,
    review: "",
    status: "Review",
    saving: false,
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("https://dev.sliitleo.org/Backend/tickets_admin.php?mode=get");
      // some hosts return text on errors; try to parse safely
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = [];
      }
      const list = toArray(data).map((t) => ({
        id: t?.id ?? t?.ticket_id ?? "",
        subject: t?.subject ?? "(No subject)",
        user_name: t?.user_name ?? "",
        user_id: t?.user_id ?? "",
        created_at: t?.created_at ?? t?.createdAt ?? "",
        description: t?.description ?? "",
        review: t?.review ?? "",
        status: t?.status ?? "Review",
      }));
      setTickets(list);
    } catch (e) {
      setError("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const closeModal = () =>
    setModal({ open: false, ticket: null, review: "", status: "Review", saving: false });

  const openReview = (ticket) => {
    setModal({
      open: true,
      ticket,
      review: ticket?.review || "",
      status: ticket?.status || "Review",
      saving: false,
    });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!modal.ticket || !modal.ticket.id) {
      alert("Error: Invalid ticket data. Please try again.");
      return;
    }
    const payload = {
      id: parseInt(String(modal.ticket.id), 10) || modal.ticket.id,
      status: modal.status || "Review",
      review: modal.review || "",
      review_id: user?.SLIITID || "0",
      review_name: user?.name || "System",
    };

    try {
      setModal((m) => ({ ...m, saving: true }));
      const res = await fetch("https://dev.sliitleo.org/Backend/tickets_admin.php?mode=review", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      // success
      setModal({ open: false, ticket: null, review: "", status: "Review", saving: false });
      fetchTickets();
    } catch (err) {
      console.error("Error updating review:", err);
      alert(`Failed to update ticket review: ${err?.message || "Unknown error"}`);
      setModal((m) => ({ ...m, saving: false }));
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <span className="spinner lg" />
        <p>Loading tickets…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-banner" role="alert" style={{ margin: "var(--sp-4)" }}>
        <FontAwesomeIcon icon={faExclamationCircle} />
        <span>{error}</span>
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className="empty-state">
        <div className="empty-icon"><FontAwesomeIcon icon={faInbox} /></div>
        <h3>No tickets found</h3>
        <p>Member-submitted tickets will show up here.</p>
      </div>
    );
  }

  return (
    <>
      <div style={{ overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th scope="col">Subject</th>
              <th scope="col">User</th>
              <th scope="col">Created</th>
              <th scope="col">Review</th>
              <th scope="col">Status</th>
              <th scope="col" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket) => (
              <tr
                key={String(ticket.id)}
                style={{ cursor: "pointer" }}
                tabIndex={0}
                role="button"
                aria-label={`Review ticket: ${ticket.subject}`}
                onClick={() => openReview(ticket)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openReview(ticket);
                  }
                }}
              >
                <td>
                  <div className="proj-name">
                    {ticket.subject}{" "}
                    <span className="num" style={{ color: "var(--text-3)", fontWeight: 400 }}>
                      #{ticket.id}
                    </span>
                  </div>
                  <div className="proj-cat truncate" style={{ maxWidth: 320 }} title={ticket.description}>
                    {ticket.description || "—"}
                  </div>
                </td>
                <td>{ticket.user_name || ticket.user_id || "—"}</td>
                <td className="num" style={{ color: "var(--text-2)" }}>{ticket.created_at || "—"}</td>
                <td className="truncate" style={{ maxWidth: 220 }} title={ticket.review}>
                  {ticket.review && String(ticket.review).trim().length ? ticket.review : "No review"}
                </td>
                <td><StatusPill status={ticket.status} /></td>
                <td className="row-actions">
                  <button
                    type="button"
                    className="icon-btn"
                    aria-label={`Open review for ticket #${ticket.id}`}
                    onClick={(e) => { e.stopPropagation(); openReview(ticket); }}
                  >
                    <FontAwesomeIcon icon={faChevronRight} size="xs" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal.open && (
        <div className="modal-overlay" onClick={closeModal}>
          <form
            className="modal-panel panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reviewTicketTitle"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitReview}
          >
            <div className="modal-head">
              <h3 id="reviewTicketTitle">Review Ticket</h3>
              <button type="button" className="icon-btn" aria-label="Close" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} size="xs" />
              </button>
            </div>

            <div className="modal-body">
              {modal.ticket && (
                <div style={{ marginBottom: "var(--sp-4)" }}>
                  <div className="proj-name">
                    {modal.ticket.subject}{" "}
                    <span className="num" style={{ color: "var(--text-3)", fontWeight: 400 }}>
                      #{modal.ticket.id}
                    </span>
                  </div>
                  <div className="proj-cat">{modal.ticket.user_name || modal.ticket.user_id || "—"}</div>
                  {modal.ticket.description && (
                    <p style={{ marginTop: "var(--sp-2)", fontSize: 13, color: "var(--text-2)", whiteSpace: "pre-line" }}>
                      {modal.ticket.description}
                    </p>
                  )}
                </div>
              )}

              <div className="field">
                <label className="field-label" htmlFor="review-comments">Review Comments</label>
                <textarea
                  id="review-comments"
                  rows={4}
                  value={modal.review}
                  onChange={(e) => setModal((m) => ({ ...m, review: e.target.value }))}
                />
              </div>

              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="review-status">Status</label>
                <select
                  id="review-status"
                  value={modal.status}
                  onChange={(e) => setModal((m) => ({ ...m, status: e.target.value }))}
                >
                  <option value="Pending">Pending</option>
                  <option value="Review">Review</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>

            <div className="modal-foot">
              <button type="button" className="btn btn-ghost" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={modal.saving}>
                {modal.saving && <span className="spinner" />} {modal.saving ? "Saving…" : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/* ============================================================
   Page Shell
============================================================ */
export default function ClubStatistics() {
  const user = readUser();
  const [members, setMembers] = useState([]);
  const [mLoading, setMLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Explicit limit: the endpoint defaults to 4000 rows with none
        // passed, silently truncating the real roster (4085+ members).
        const res = await fetch("https://dev.sliitleo.org/Backend/member_api.php?action=list_basic&limit=40000");
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          data = [];
        }
        setMembers(toArray(data));
      } catch {
        setMembers([]);
      } finally {
        setMLoading(false);
      }
    })();
  }, []);

  // Make sure downstream .filter/.map never crash
  const memberList = useMemo(() => (Array.isArray(members) ? members : []), [members]);

  // Example: compute newcomers safely (won’t crash if API changes)
  const now = new Date();
  const newcomersMonth = memberList.filter((m) => {
    const d = safeDate(m?.RegDate);
    return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // (The counts aren’t displayed here, but leaving the logic shows how to compute without crashes.)

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="page-head">
          <div>
            <div className="eyebrow">Administration</div>
            <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <FontAwesomeIcon icon={faTicketAlt} /> Tickets Admin
            </h1>
            <p className="page-desc">Review, respond to, and update the status of member support tickets.</p>
          </div>
        </div>

        <div className="filter-row" style={{ flexWrap: "wrap", marginBottom: "var(--sp-6)" }}>
          <Link to="/club-statistics" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartPie} /> Club Statistics
          </Link>
          <Link to="/reporting-hub" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faChartBar} /> Reporting Hub
          </Link>
          <Link to="/member-database" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faUsers} /> Member Database
          </Link>
          <Link to="/category-selection" className="btn btn-ghost btn-sm">
            <FontAwesomeIcon icon={faListAlt} /> Category Selections
          </Link>
          <Link to="/tickets-admin" className="btn btn-primary btn-sm">
            <FontAwesomeIcon icon={faTicketAlt} /> Tickets Admin
          </Link>
        </div>

        <div className="panel">
          <div className="section-head">
            <h2>All Tickets (Admin View)</h2>
          </div>
          <TicketListAdmin />
        </div>
      </main>
    </div>
  );
}
